/**
 * `@ronceray/contract` — the error model (US-016, FR-25).
 *
 * Three error bodies, and one mapper. Nothing else in this repository may define these shapes.
 *
 * ------------------------------------------------------------------------------------------------
 * THE MOST IMPORTANT RULE IN THIS FILE: **A FAILED ENGINEERING CHECK RETURNS `200`.**
 * ------------------------------------------------------------------------------------------------
 *
 * A member that does not verify is a **result**, not an error. `ratio > 1`, `status: 'ko'`, a
 * deflection over `L/250`, a beam that fails every single check — all of these are perfectly
 * successful calculations, and the API answers them with `200` and the normal response envelope
 * (see `./response`). An API that returned `422` for an overstressed beam would be wrong: it would
 * be telling the engineer "your request was malformed" when in fact the request was fine and the
 * answer is "this section does not work". It would also make the failure indistinguishable, at the
 * transport layer, from a typo in a field name — and the whole point of a verification tool is that
 * "does not verify" is the interesting, expected, load-bearing answer.
 *
 * The error bodies below are therefore reserved strictly for **request and account** problems:
 *
 *   - `422` the request was not a valid calculation input   (`invalidInputErrorSchema`)
 *   - `404` the request named a catalog entry we do not have (`unknownCatalogEntryErrorSchema`)
 *   - `429` the caller is out of quota                       (`quotaExceededErrorSchema`)
 *
 * Nothing about the engineering outcome ever reaches this file. `ERRORS.md` repeats this rule for
 * readers who arrive at the documentation instead of the source.
 *
 * @see ERRORS.md — one concrete example body per case, plus the field-path format.
 */

import { z } from 'zod';

import { savedModuleSchema } from './saved-file';

/* ================================================================================================
 * Field paths
 * ============================================================================================== */

/**
 * The string used as the field path of an issue that belongs to the request as a whole rather than
 * to any one field — a cross-field refinement (`Lcry` may not exceed `L`), or a body that is not an
 * object at all. Chosen so it can never collide with a real path: no field id in this contract
 * contains a parenthesis.
 */
export const ROOT_FIELD_PATH = '(root)';

/**
 * Format a Zod issue path as the dotted path used by the `422` body.
 *
 * The format, which `ERRORS.md` documents for API consumers and which must stay stable because
 * clients highlight form fields by matching against it:
 *
 * | `issue.path`             | `path` in the body |
 * | ------------------------ | ------------------ |
 * | `['profil']`             | `profil`           |
 * | `['cas', 'lamine', 'L']` | `cas.lamine.L`     |
 * | `['charges', 2, 'q']`    | `charges[2].q`     |
 * | `[]`                     | `(root)`           |
 *
 * Object keys are joined with `.`; array indices are appended as `[n]` rather than as another
 * dotted segment, so a consumer can tell "the third element of `charges`" from "the key `2` of
 * `charges`" without knowing the schema. Zod types a path segment as `PropertyKey`, so a symbol is
 * possible in principle; it is stringified rather than dropped, because silently losing a segment
 * would produce a path that points at the wrong field.
 */
export function formatIssuePath(path: readonly PropertyKey[]): string {
  if (path.length === 0) return ROOT_FIELD_PATH;

  let out = '';
  for (const segment of path) {
    if (typeof segment === 'number') {
      out += `[${segment}]`;
    } else {
      out += out === '' ? String(segment) : `.${String(segment)}`;
    }
  }
  // A path that begins with an index (`[0].q`) still needs to be non-empty and unambiguous; the
  // bracket form already is, so there is nothing to fix up here.
  return out;
}

/* ================================================================================================
 * Error bodies
 * ============================================================================================== */

/**
 * Machine-readable discriminants. Clients branch on `error`, never on the prose `message` and never
 * on the HTTP status alone — the status is a category, `error` is the reason.
 */
/**
 * P2/US-205 note: the `401 unauthenticated` body lives in `./auth`, not here, because it is the one
 * ADDITION to this frozen file's error set and it should be readable as such — with its written
 * justification beside it — rather than blended into the P0 originals. `apiErrorSchema` below is
 * unchanged, and stays unchanged: it is the frozen answer to "what can a *calculation* return".
 *
 * P2 adds two more bodies that this union deliberately does not describe — `401 unauthenticated`
 * in `./auth`, and `404 not_found` further down this file. There is no widened union type, because
 * no consumer has needed one: every caller knows which of these a given route can produce. An
 * earlier draft of this comment promised an `anyApiErrorSchema`; it was never built, and inventing
 * it now would be a shape with no reader.
 */
export const API_ERROR_CODES = [
  'invalid_input',
  'unknown_catalog_entry',
  'quota_exceeded',
] as const;

export const apiErrorCodeSchema = z.enum(API_ERROR_CODES);
export type ApiErrorCode = z.infer<typeof apiErrorCodeSchema>;

/**
 * One per-field message inside a `422`. There may be several entries for the same `path`: a value
 * can fail more than one rule, and collapsing them would hide the second reason.
 */
export const fieldErrorSchema = z.object({
  /** Dotted field path — see {@link formatIssuePath} for the exact format. */
  path: z.string().min(1),
  /** Human-readable, already localised by whatever produced it. Shown next to the input. */
  message: z.string().min(1),
  /**
   * The Zod issue code that produced this entry (`invalid_type`, `too_small`, `invalid_value`…).
   * Deliberately a free string, not an enum: Zod's issue-code set is Zod's to extend, and a client
   * that has never heard of a new code must degrade to showing `message`, not fail to parse the
   * error body.
   */
  code: z.string().min(1),
});
export type FieldError = z.infer<typeof fieldErrorSchema>;

/**
 * `422 Unprocessable Content` — the request was not a valid calculation input.
 *
 * This is the *only* thing a validation failure produces. It is never produced by the engine, and
 * never by a check that does not verify (see the file header).
 */
export const invalidInputErrorSchema = z.object({
  /** Mirrors the HTTP status so a body pulled out of a log is still self-describing. */
  status: z.literal(422),
  error: z.literal('invalid_input'),
  /** Summary line. The actionable detail is in `fields`. */
  message: z.string().min(1),
  /**
   * At least one entry — a `422` with no field errors would be a bug in the producer, so the schema
   * refuses to represent it.
   */
  fields: z.array(fieldErrorSchema).min(1),
});
export type InvalidInputError = z.infer<typeof invalidInputErrorSchema>;

/** Which catalog table was consulted when the name could not be found. */
export const CATALOG_TABLES = ['profils', 'nuances', 'sectionsCreuses', 'classesBois'] as const;
export const catalogTableSchema = z.enum(CATALOG_TABLES);
export type CatalogTable = z.infer<typeof catalogTableSchema>;

/**
 * `404 Not Found` — the request named a profile, grade, hollow section or timber class that is not
 * in the catalog of the pinned `catalogVersion`.
 *
 * Distinct from `422` on purpose. `L: -3` is a malformed request; `profil: "IPE 999"` is a
 * well-formed request for something that does not exist, and the useful answer names the table that
 * was searched so the caller can re-read the right dropdown. Note that the catalog is public data
 * and ships to the browser, so echoing the requested `name` back leaks nothing.
 */
export const unknownCatalogEntryErrorSchema = z.object({
  status: z.literal(404),
  error: z.literal('unknown_catalog_entry'),
  message: z.string().min(1),
  /** The table searched. */
  catalog: catalogTableSchema,
  /** The name as the client sent it, verbatim — the client needs to see its own value echoed. */
  name: z.string(),
  /** Request field that carried it, in {@link formatIssuePath} format (`profil`, `nuance`, …). */
  field: z.string().min(1),
  /**
   * Catalog edition searched (`CATALOG_VERSION`, e.g. `"2026.08"`). Without it, "unknown profile"
   * is unanswerable a year later: the caller cannot tell a typo from an entry that was added since.
   */
  catalogVersion: z.string().min(1),
});
export type UnknownCatalogEntryError = z.infer<typeof unknownCatalogEntryErrorSchema>;

/**
 * `429 Too Many Requests` — the organisation's monthly cost-unit quota is spent
 * (ARCHITECTURE.md §8.3).
 *
 * Carries the reset date, because "come back later" without a date is not an answer an engineer can
 * plan around, and because a quota breach is a sales conversation rather than an incident.
 */
export const quotaExceededErrorSchema = z.object({
  status: z.literal(429),
  error: z.literal('quota_exceeded'),
  message: z.string().min(1),
  /**
   * When the quota window rolls over, as an ISO-8601 UTC instant (`2026-09-01T00:00:00Z`). An
   * offset is accepted so a future non-UTC deployment is not a breaking change, but producers emit
   * `Z`. It is an instant, not a bare date, because "the first of the month" is ambiguous across
   * time zones and this value is displayed to the user.
   */
  resetAt: z.iso.datetime({ offset: true }),
});
export type QuotaExceededError = z.infer<typeof quotaExceededErrorSchema>;

/**
 * Any error body this API produces. Discriminated on `error` (the semantic reason) rather than on
 * `status` (the transport category), so adding a second `404` reason later is additive.
 */
export const apiErrorSchema = z.discriminatedUnion('error', [
  invalidInputErrorSchema,
  unknownCatalogEntryErrorSchema,
  quotaExceededErrorSchema,
]);
export type ApiError = z.infer<typeof apiErrorSchema>;

/* ================================================================================================
 * The Zod-error -> 422 mapper
 * ============================================================================================== */

/** Default summary line for a `422`. Overridable per call; the detail is always in `fields`. */
export const DEFAULT_INVALID_INPUT_MESSAGE = 'The request contains invalid fields.';

/**
 * **THE ONLY IMPLEMENTATION OF THE `422` BODY SHAPE IN THIS REPOSITORY (FR-25).**
 *
 * P1's API imports this function; it does not re-derive the mapping, does not hand-roll
 * `error.issues.map(...)` in a middleware, and does not build the body inline in a route handler.
 * If a second version of this mapping ever appears anywhere in the repo, that duplicate is the bug
 * — deleting it and importing this is always the fix (success metric M9). The same applies to the
 * typed client in `apps/web`, which must render `InvalidInputError` rather than reading Zod issues
 * itself: the browser is not where the wire format gets decided.
 *
 * ## Zod 4 note
 *
 * Zod 4 exposes `error.issues`, each issue carrying `path: PropertyKey[]`, `message` and `code`.
 * The Zod 3 alias `error.errors` no longer exists, which is exactly why this mapping lives behind a
 * function: a Zod upgrade is then one edit here, not a sweep through every route.
 *
 * ## Mapping rules
 *
 * - One `fields` entry per Zod issue, in Zod's own order (schema declaration order), so the
 *   response is deterministic and diffable in tests.
 * - `path` is formatted by {@link formatIssuePath}; an issue with an empty path (a cross-field
 *   refinement, or a body that is not an object) becomes {@link ROOT_FIELD_PATH}.
 * - `message` is passed through verbatim. This function does not rewrite, translate or prettify
 *   messages: the wording belongs to the schema that raised it, and rewriting here would put the
 *   user-facing text somewhere no schema author would think to look.
 * - A union issue (`invalid_union`) surfaces as a single entry at the union's own path. Zod nests
 *   the per-alternative issues inside it; flattening them would emit contradictory messages for one
 *   field ("expected string" *and* "expected number"), which is worse than the one message the
 *   union itself carries.
 *
 * @param error any Zod error — typed as the core error so a `ZodError<T>` for any `T` is accepted.
 * @param message optional summary line, e.g. one localised for the caller.
 * @returns a `422` body that satisfies {@link invalidInputErrorSchema}.
 */
export function zodErrorTo422Body(
  error: z.core.$ZodError,
  message: string = DEFAULT_INVALID_INPUT_MESSAGE,
): InvalidInputError {
  const fields: FieldError[] = error.issues.map((issue) => ({
    path: formatIssuePath(issue.path),
    message: issue.message,
    code: issue.code,
  }));

  return {
    status: 422,
    error: 'invalid_input',
    message,
    // A ZodError with zero issues is not a thing Zod produces, but `fields` is declared non-empty,
    // so make the impossible case explicit rather than emitting a body that fails our own schema.
    fields:
      fields.length > 0
        ? fields
        : [{ path: ROOT_FIELD_PATH, message, code: 'invalid_input' } satisfies FieldError],
  };
}

/**
 * The `422` for a body that is not JSON at all.
 *
 * P1 had this literal inlined in `routes/calc.ts`; P2 wave 2 added a second copy in `routes/org.ts`;
 * wave 3 adds four more routes that read a JSON body. Six copies of an error body is precisely the
 * duplication metric M9 names, and the failure mode is not hypothetical — the copies had already
 * begun to drift in whitespace. One constant, imported.
 *
 * It is a `422` rather than a `400` for consistency with every other rejected body: the client sent
 * something, the something was not usable, and the field list says where. A route that answered
 * `400` here and `422` one line later would be describing the same class of mistake two ways.
 */
/**
 * `404 Not Found` — and, in this API, also "you may not have this" (P2/US-203).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * NOT FOUND, NEVER FORBIDDEN
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * Another organisation's case, a project that never existed, and a route only an admin may call all
 * produce **this same body**. Answering `403` would confirm that the id is real, or that the route
 * exists and the caller is one role away from it — a disclosure an attacker with a list of ids can
 * work with. The cost is that a genuinely confused user gets a slightly less helpful message, and
 * that is the right side of the trade for a tool holding tender calculations.
 *
 * It is **not** a member of {@link apiErrorSchema}. That union is P0-frozen and describes what a
 * *calculation* can return; `not_found` predates this schema in the routes (`calc.ts` has emitted it
 * since P1 for an unknown module) but had no written shape until now, which is why the body was
 * being retyped at each call site.
 */
export const notFoundErrorSchema = z.object({
  status: z.literal(404),
  error: z.literal('not_found'),
  message: z.string().min(1),
});
export type NotFoundError = z.infer<typeof notFoundErrorSchema>;

/** The one implementation of the `404` body. See {@link notFoundErrorSchema}. */
export function notFoundBody(message: string): NotFoundError {
  return { status: 404, error: 'not_found', message };
}

export const NOT_JSON_BODY: InvalidInputError = {
  status: 422,
  error: 'invalid_input',
  message: 'The request body is not valid JSON.',
  fields: [{ path: ROOT_FIELD_PATH, message: 'Expected a JSON object.', code: 'invalid_type' }],
};

/**
 * Convenience view of a `422` for form rendering: field path -> its messages, in wire order.
 *
 * This is a *function*, not a second schema. The wire shape stays the ordered `fields` array (order
 * carries information, and one field may legitimately have several messages); a client that wants
 * lookup-by-path derives it here rather than the API shipping the same information twice.
 */
export function groupFieldMessages(body: InvalidInputError): Record<string, string[]> {
  const grouped: Record<string, string[]> = {};
  for (const field of body.fields) {
    const existing = grouped[field.path];
    if (existing) existing.push(field.message);
    else grouped[field.path] = [field.message];
  }
  return grouped;
}

/* ================================================================================================
 * The committed invalid-input case set (US-016 / FR-26; consumed by US-017)
 * ============================================================================================== */

/**
 * Shape of one JSON file in `packages/contract/invalid-cases/`.
 *
 * It lives here rather than in the test that reads it, for the same reason everything else in this
 * package does: the case files are a shape in this repository, and a shape defined twice is the
 * bug. US-017 parses each file with this schema before running it, so a malformed case file fails
 * as a bad fixture with a field path, not as a mysterious assertion.
 *
 * `invalid-cases/README.md` documents the format in prose for a human adding a case.
 */
export const invalidInputCaseSchema = z.object({
  /** Unique, stable, file-name-shaped: `<module>-<kind>-<field>`. Used in test names. */
  id: z.string().min(1),
  /**
   * Reuses the module enum from `./saved-file` rather than restating the three names: the domain is
   * the same one, and this package's whole premise is that no shape is written twice.
   */
  module: savedModuleSchema,
  kind: z.enum(['missing-required', 'out-of-range', 'wrong-type', 'unknown-catalog-entry']),
  /** `404` for `unknown-catalog-entry`, `422` for the other three. */
  expectedStatus: z.union([z.literal(422), z.literal(404)]),
  /** One line: what is wrong and why the request schema must reject it. */
  rationale: z.string().min(1),
  /**
   * The request body, as a client would send it. Loosely typed on purpose — these payloads are
   * *invalid* by construction, so the only schema that could describe them is `unknown`.
   */
  input: z.record(z.string(), z.unknown()),
  /**
   * Present only on `unknown-catalog-entry` cases: which field carries the unknown name, what the
   * name is, and which catalog table should have contained it. It lets US-017 assert the resulting
   * `404` body precisely instead of inferring the field from the case id.
   */
  unknown: z
    .object({
      field: z.string().min(1),
      value: z.string(),
      catalog: catalogTableSchema,
    })
    .optional(),
});
export type InvalidInputCase = z.infer<typeof invalidInputCaseSchema>;
